import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import softwareCart, {
  CartProduct,
  SOFTWARE_CART_CHANGE_EVENT,
  SOFTWARE_CART_MAX_ITEMS,
} from "../SoftwareCart";

interface Props {
  product: CartProduct;
  labelAdd: string;
  labelRemove: string;
  labelLimitTooltip: string;
  labelAddedToast: string;
  // URL of the download cart page (same target as the header cart icon).
  cartUrl: string;
}

const formatLabel = (
  template: string,
  ...values: (string | number)[]
): string =>
  values.reduce<string>(
    (acc, value, index) => acc.replace(`{${index}}`, String(value)),
    template
  );

const IconDownload: React.FunctionComponent = () => (
  <span className="software-cart-button__icon-wrap" aria-hidden="true">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="15"
      viewBox="0 0 19 19"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M18.75 11.625V17.625C18.75 17.9234 18.6315 18.2095 18.4205 18.4205C18.2095 18.6315 17.9234 18.75 17.625 18.75H1.125C0.826631 18.75 0.540483 18.6315 0.329505 18.4205C0.118526 18.2095 0 17.9234 0 17.625V11.625C0 11.3266 0.118526 11.0405 0.329505 10.8295C0.540483 10.6185 0.826631 10.5 1.125 10.5C1.42337 10.5 1.70952 10.6185 1.9205 10.8295C2.13147 11.0405 2.25 11.3266 2.25 11.625V16.5H16.5V11.625C16.5 11.3266 16.6185 11.0405 16.8295 10.8295C17.0405 10.6185 17.3266 10.5 17.625 10.5C17.9234 10.5 18.2095 10.6185 18.4205 10.8295C18.6315 11.0405 18.75 11.3266 18.75 11.625ZM8.57906 12.4209C8.68358 12.5258 8.80777 12.609 8.94452 12.6658C9.08126 12.7226 9.22787 12.7518 9.37594 12.7518C9.524 12.7518 9.67061 12.7226 9.80736 12.6658C9.9441 12.609 10.0683 12.5258 10.1728 12.4209L13.9228 8.67094C14.1342 8.45959 14.2529 8.17295 14.2529 7.87406C14.2529 7.57518 14.1342 7.28853 13.9228 7.07719C13.7115 6.86584 13.4248 6.74711 13.1259 6.74711C12.8271 6.74711 12.5404 6.86584 12.3291 7.07719L10.5 8.90625V1.125C10.5 0.826631 10.3815 0.540483 10.1705 0.329505C9.95952 0.118526 9.67337 0 9.375 0C9.07663 0 8.79048 0.118526 8.5795 0.329505C8.36853 0.540483 8.25 0.826631 8.25 1.125V8.90625L6.42094 7.07906C6.31629 6.97442 6.19206 6.8914 6.05533 6.83477C5.9186 6.77814 5.77206 6.74899 5.62406 6.74899C5.32518 6.74899 5.03853 6.86772 4.82719 7.07906C4.72254 7.18371 4.63953 7.30794 4.5829 7.44467C4.52626 7.5814 4.49711 7.72794 4.49711 7.87594C4.49711 8.17482 4.61584 8.46147 4.82719 8.67281L8.57906 12.4209Z"
      />
    </svg>
  </span>
);

const IconTrash: React.FunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 448 512"
    aria-hidden="true"
    focusable="false"
    className="software-cart-button__trash-icon"
  >
    <path
      fill="currentColor"
      d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"
    />
  </svg>
);

const getOrCreateToastPortal = (): HTMLElement => {
  let el = document.getElementById("software-cart-toast-portal");
  if (el === null) {
    el = document.createElement("div");
    el.id = "software-cart-toast-portal";
    document.body.appendChild(el);
  }
  return el;
};

const Toast: React.FunctionComponent<{ label: string; cartUrl: string }> = ({
  label,
  cartUrl,
}) => {
  const el = getOrCreateToastPortal();
  const content = (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 512 512"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
        />
      </svg>
      {label}
    </>
  );
  return ReactDOM.createPortal(
    cartUrl !== "" ? (
      <a
        href={cartUrl}
        className="software-cart-toast software-cart-toast--link"
        aria-live="polite"
      >
        {content}
      </a>
    ) : (
      <div className="software-cart-toast" role="status" aria-live="polite">
        {content}
      </div>
    ),
    el
  );
};

const SoftwareCartButton: React.FunctionComponent<Props> = ({
  product,
  labelAdd,
  labelRemove,
  labelLimitTooltip,
  labelAddedToast,
  cartUrl,
}) => {
  const [inCart, setInCart] = useState(() => softwareCart.has(product.uid));
  const [count, setCount] = useState(() => softwareCart.getCount());
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      setInCart(softwareCart.has(product.uid));
      setCount(softwareCart.getCount());
    };
    document.addEventListener(SOFTWARE_CART_CHANGE_EVENT, handleChange);
    return () =>
      document.removeEventListener(SOFTWARE_CART_CHANGE_EVENT, handleChange);
  }, [product.uid]);

  useEffect(() => {
    if (!showToast) return undefined;
    const timer = setTimeout(() => setShowToast(false), 3000);
    return () => clearTimeout(timer);
  }, [showToast]);

  // Limit only blocks adding new products, never removing one already in the cart
  const atLimit = !inCart && count >= SOFTWARE_CART_MAX_ITEMS;

  const handleClick = (event: React.MouseEvent) => {
    // Keep the surrounding accordion open instead of toggling it
    event.stopPropagation();
    if (atLimit) return;
    const result = softwareCart.toggle(product);
    if (result === "added") setShowToast(true);
  };

  const button = (
    <button
      type="button"
      className={`software-cart-button${
        inCart ? " software-cart-button--active" : ""
      }${atLimit ? " software-cart-button--disabled" : ""}`}
      onClick={handleClick}
      aria-pressed={inCart}
      aria-disabled={atLimit || undefined}
    >
      {inCart ? <IconTrash /> : <IconDownload />}
      <span className="software-cart-button__label">
        {inCart ? labelRemove : labelAdd}
      </span>
    </button>
  );

  return (
    <div className="software-cart-button-wrapper">
      {atLimit ? (
        <span className="tooltipfast">
          {button}
          <span className="tooltiptextfast">
            {formatLabel(labelLimitTooltip, count, SOFTWARE_CART_MAX_ITEMS)}
          </span>
        </span>
      ) : (
        button
      )}
      {showToast && <Toast label={labelAddedToast} cartUrl={cartUrl} />}
    </div>
  );
};

export default SoftwareCartButton;
